import { Box } from '@mui/material';
import { differenceInMonths } from 'date-fns';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductSourcingModes } from 'entities/ProductSourcing';
import { toNumber } from 'shared/lib/number/number';
import { parseUtcIsoToDate } from 'shared/lib/reportDatePresets';
import {
    ComposableReportWidget,
    type ReportWidgetColumnConfig,
    type ReportWidgetConfig,
    type ReportWidgetInteractionPayload,
    type ReportWidgetItem,
    type ReportWidgetTooltipRowConfig,
    type ReportWidgetTooltipRowsBuilder,
    augmentItemsForTopNOther,
    buildTopNChartItemsWithOther,
} from 'widgets/ComposableReportWidget';
import {
    buildPrimaryMetricRow,
    getTooltipPointFromPayload,
} from 'widgets/ComposableReportWidget/model/lib/tooltipHelpers';

import type { Property } from 'entities/Property/model/types/PropertySchema';
import type { SpendPropertyRowDto } from 'entities/ReportSpendSummary';

const PIE_MAX_ITEMS = 5;
const TABLE_INITIAL_LIMIT = 5;

type SpendSummaryPropertiesTableColumnId =
    | 'property'
    | 'productSourcing'
    | 'units'
    | 'orders'
    | 'totalSpend'
    | 'spend'
    | 'spendPerUnit'
    | 'spendPerUnitMo'
    | 'percentOfTotal'
    | 'percentSpend';

function getProductSourcingLabel(
    mode: string | undefined,
    t: (key: string) => string,
): string {
    if (mode === ProductSourcingModes.AvailabilityPrice) {
        return t('Best Price');
    }

    return mode ? t('Preferred') : '-';
}

function propertyToWidgetItem(
    dto: SpendPropertyRowDto,
    property: Property | undefined,
    t: (key: string) => string,
    monthsInRange: number,
): ReportWidgetItem {
    const spendNum = toNumber(dto.totalSpend);
    const unitCount = property?.unitCount ?? null;
    const spendPerUnit =
        unitCount != null && unitCount > 0 ? spendNum / unitCount : '';
    const spendPerUnitMo =
        monthsInRange > 0 && unitCount != null && unitCount > 0
            ? spendNum / unitCount / monthsInRange
            : '';

    return {
        id: dto.propertyId,
        label: dto.propertyName?.trim() || dto.propertyId,
        primaryValue: spendNum,
        secondaryValues: {
            label: dto.propertyName?.trim() || dto.propertyId,
            productSourcing: getProductSourcingLabel(
                property?.sourcingSettings?.mode,
                t,
            ),
            units: unitCount ?? '',
            orderCount: dto.orderCount,
            totalSpend: dto.totalSpend,
            spendPerUnit,
            spendPerUnitMo,
            percentOfTotal: dto.percentOfTotal,
        },
        meta: { propertyId: dto.propertyId, property },
    };
}

interface SpendSummaryPropertiesSectionProps {
    byProperties: SpendPropertyRowDto[];
    properties: Property[];
    loading?: boolean;
    dateFrom: string;
    dateTo: string;
    disableChartAnimation?: boolean;
    filtersKey?: string;
    showBottomBorder?: boolean;
    tableOnly?: boolean;
    tableColumnIds?: SpendSummaryPropertiesTableColumnId[];
    onPropertySelect?: (args: {
        propertyId: string;
        propertyName?: string;
    }) => void;
}

export const SpendSummaryPropertiesSection = memo(
    ({
        byProperties,
        properties,
        loading = false,
        dateFrom,
        dateTo,
        disableChartAnimation = false,
        filtersKey,
        showBottomBorder = true,
        tableOnly = false,
        tableColumnIds,
        onPropertySelect,
    }: SpendSummaryPropertiesSectionProps) => {
        const { t } = useTranslation();

        const filteredByProperties = useMemo(
            () => byProperties.filter((dto) => toNumber(dto.totalSpend) > 0),
            [byProperties],
        );

        const propertyMap = useMemo(
            () => new Map(properties.map((p) => [p.id, p])),
            [properties],
        );

        const monthsInRange = useMemo(
            () =>
                Math.max(
                    1,
                    differenceInMonths(
                        parseUtcIsoToDate(dateTo),
                        parseUtcIsoToDate(dateFrom),
                    ) + 1,
                ),
            [dateFrom, dateTo],
        );

        const items: ReportWidgetItem[] = useMemo(() => {
            const sorted = [...filteredByProperties]
                .sort((a, b) => toNumber(b.totalSpend) - toNumber(a.totalSpend))
                .map((dto) =>
                    propertyToWidgetItem(
                        dto,
                        propertyMap.get(dto.propertyId),
                        t,
                        monthsInRange,
                    ),
                );

            if (tableOnly) {
                return sorted;
            }

            return augmentItemsForTopNOther({
                items: sorted,
                topCount: PIE_MAX_ITEMS,
                otherChartItemId: 'spend-summary-properties-other',
            });
        }, [filteredByProperties, propertyMap, t, monthsInRange, tableOnly]);

        const chartItems: ReportWidgetItem[] | undefined = useMemo(() => {
            if (tableOnly || items.length <= PIE_MAX_ITEMS) return undefined;

            return buildTopNChartItemsWithOther({
                items,
                topCount: PIE_MAX_ITEMS,
                otherItemId: 'spend-summary-properties-other',
                otherLabel: t('Other'),
            });
        }, [items, t, tableOnly]);

        const handleInteraction = useCallback(
            (payload: ReportWidgetInteractionPayload) => {
                if (payload.item.meta?.isAggregatedOther) return;
                const propertyId = payload.item.meta?.propertyId;

                if (typeof propertyId !== 'string') return;

                onPropertySelect?.({
                    propertyId,
                    propertyName: payload.item.label,
                });
            },
            [onPropertySelect],
        );

        const tableColumns = useMemo<ReportWidgetColumnConfig[]>(() => {
            const allColumnsById: Record<
                SpendSummaryPropertiesTableColumnId,
                ReportWidgetColumnConfig
            > = {
                property: {
                    id: 'property',
                    label: t('Property'),
                    valueKey: 'label',
                    format: 'text',
                    align: 'left',
                    bold: true,
                    minWidth: 200,
                    maxWidth: 240,
                    allowWrap: true,
                },
                productSourcing: {
                    id: 'productSourcing',
                    label: t('spendSummary.propertiesColumnProductSourcing'),
                    valueKey: 'productSourcing',
                    format: 'text',
                    align: 'left',
                    headerTooltip: t('spendSummary.productSourcingTooltip'),
                    minWidth: 120,
                    allowWrap: true,
                },
                units: {
                    id: 'units',
                    label: t('Units'),
                    valueKey: 'units',
                    format: 'number',
                    align: 'right',
                },
                orders: {
                    id: 'orders',
                    label: t('Orders'),
                    valueKey: 'orderCount',
                    format: 'number',
                    align: 'right',
                },
                totalSpend: {
                    id: 'totalSpend',
                    label: t('spendSummary.totalSpend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                    align: 'right',
                },
                spend: {
                    id: 'spend',
                    label: t('spendSummary.spend'),
                    valueKey: 'totalSpend',
                    format: 'currency',
                    align: 'right',
                },
                spendPerUnit: {
                    id: 'spendPerUnit',
                    label: t('spendSummary.propertiesColumnSpendPerUnit'),
                    valueKey: 'spendPerUnit',
                    format: 'currency',
                    align: 'right',
                },
                spendPerUnitMo: {
                    id: 'spendPerUnitMo',
                    label: t('spendSummary.propertiesColumnSpendPerUnitMo'),
                    valueKey: 'spendPerUnitMo',
                    format: 'currency',
                    align: 'right',
                },
                percentOfTotal: {
                    id: 'percentOfTotal',
                    label: t('spendSummary.percentOfTotal'),
                    valueKey: 'percentOfTotal',
                    format: 'percent',
                    align: 'right',
                },
                percentSpend: {
                    id: 'percentSpend',
                    label: t('spendSummary.percentSpend'),
                    valueKey: 'percentOfTotal',
                    format: 'percent',
                    align: 'right',
                },
            };

            const defaultOrder: SpendSummaryPropertiesTableColumnId[] = [
                'property',
                'productSourcing',
                'units',
                'orders',
                'totalSpend',
                'spendPerUnit',
                'spendPerUnitMo',
            ];

            const orderedIds = tableColumnIds?.length
                ? tableColumnIds
                : defaultOrder;

            return orderedIds
                .map((id) => allColumnsById[id])
                .filter((column): column is ReportWidgetColumnConfig =>
                    Boolean(column),
                );
        }, [t, tableColumnIds]);

        const config: ReportWidgetConfig = useMemo(() => {
            const tooltipRowsBuilder: ReportWidgetTooltipRowsBuilder = (
                args,
            ) => {
                if (args.aggregation?.kind === 'other') {
                    const entry = args.payload?.[0];
                    const aggregatedValue = toNumber(
                        entry?.value as number | string | null,
                    );

                    return [
                        ...args.aggregation.headerRows,
                        buildPrimaryMetricRow({
                            id: 'spend',
                            label: t('spendSummary.totalSpend'),
                            value: args.valueFormatter(aggregatedValue),
                            valueColor: entry?.color ?? 'metrics.base',
                        }),
                    ];
                }

                const resolved = getTooltipPointFromPayload(args);
                const item = resolved?.item;

                if (!item) {
                    return null;
                }

                const spendPerUnitNum = toNumber(
                    item.secondaryValues?.spendPerUnit,
                );

                const rows: ReportWidgetTooltipRowConfig[] = [
                    {
                        id: 'header',
                        variant: 'header',
                        label: item.label,
                        boldLabel: true,
                        mutedLabel: true,
                        marginBottom: 8,
                    },
                    buildPrimaryMetricRow({
                        id: 'spend',
                        label: t('spendSummary.totalSpend'),
                        value: args.valueFormatter(toNumber(item.primaryValue)),
                        valueColor: resolved.color ?? 'metrics.base',
                    }),
                ];

                if (spendPerUnitNum > 0) {
                    rows.push({
                        id: 'spendPerUnit',
                        variant: 'labelValue',
                        label: t('spendSummary.propertiesColumnSpendPerUnit'),
                        value: args.valueFormatter(spendPerUnitNum),
                    });
                }

                return rows;
            };

            return {
                widgetId: 'spend-summary-properties',
                title: t('spendSummary.propertiesSectionTitle'),
                modelLabel: t('spendSummary.modelLabelProperties'),
                layout: tableOnly ? 'stacked' : 'sideBySide',
                visualization: {
                    enabled: !tableOnly,
                    type: 'donut',
                    options: {
                        palette: 'neutral',
                        showLegend: false,
                        valueFormatter: (v) =>
                            new Intl.NumberFormat(undefined, {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            }).format(
                                typeof v === 'number'
                                    ? v
                                    : Number.parseFloat(String(v)) || 0,
                            ),
                        tooltipRowsBuilder,
                    },
                },
                pagination: {
                    mode: 'client',
                    pageSize: TABLE_INITIAL_LIMIT,
                    revealAllOnLoadMore: true,
                },
                table: {
                    enabled: true,
                    columns: tableColumns,
                    options: {
                        showColorSwatchInLabelColumn: tableOnly
                            ? undefined
                            : 'label',
                        loadMoreVariant: 'link',
                    },
                },
                onRowClick: onPropertySelect ? handleInteraction : undefined,
                onChartItemClick:
                    !tableOnly && onPropertySelect
                        ? handleInteraction
                        : undefined,
            };
        }, [t, tableColumns, tableOnly, onPropertySelect, handleInteraction]);

        return (
            <Box
                data-testclass="spend-summary-properties-section"
                data-pdf-section="true"
                sx={{
                    borderBottom: showBottomBorder
                        ? '1px solid #E0E0E0'
                        : 'none',
                    paddingBottom: showBottomBorder ? '40px' : 0,
                }}
            >
                <ComposableReportWidget
                    key={filtersKey}
                    config={config}
                    items={items}
                    chartItems={chartItems}
                    loading={loading}
                    isPrinting={disableChartAnimation}
                    disableChartAnimation={disableChartAnimation}
                />
            </Box>
        );
    },
);

SpendSummaryPropertiesSection.displayName = 'SpendSummaryPropertiesSection';
